import { useIntl } from 'react-intl';
import type { AppRunDetail } from '@api/apps';
import { axiosErrorMessage } from '@api/errors';
import useSnackbar from '@hooks/useSnackbar';
import { useAppBuilderActions } from '../useAppBuilder';

// An agent-free app has no run to keep, only the settings its page last reported, so one without settings has nothing.
export const useKeepAsExample = (
  appKey: string,
  run: AppRunDetail | null,
  agent: boolean,
  hasOptions: boolean,
  lastOptions: Readonly<Record<string, unknown>> | null,
) => {
  const intl = useIntl();
  const { showError, showSuccess } = useSnackbar();
  const { promoteExample, isPromotingExample, keepOptionsAsExample, isKeepingOptions } = useAppBuilderActions();

  const visible = agent ? Boolean(run?.canPromoteToExample) : hasOptions;
  const ready = agent ? visible : lastOptions !== null;

  const save = () => {
    const saving =
      agent && run
        ? promoteExample({ appKey, runId: run.runId })
        : keepOptionsAsExample({ appKey, options: lastOptions ?? {} });
    saving
      .then(() =>
        showSuccess(
          intl.formatMessage({
            id: 'apps.builder.run.exampleSaved',
            defaultMessage: 'Saved as the example. Publish to show it to others.',
          }),
        ),
      )
      .catch((err: unknown) =>
        showError(
          axiosErrorMessage(
            err,
            intl.formatMessage({
              id: 'apps.builder.run.exampleFailed',
              defaultMessage: 'This could not be saved as the example. Try again.',
            }),
          ),
        ),
      );
  };

  return { visible, ready, saving: isPromotingExample || isKeepingOptions, save };
};
